/**
 * sfxManifest.ts
 *
 * Maps each of the twelve notable game event types to the CC0 sound-effect clip
 * played when that event resolves (WP-412 Surface 1 coverage). The keys are
 * the `NotableGameEventType` discriminators the center-screen overlay already
 * keys on (`NotableEventOverlay.vue`); the values are absolute clip URLs.
 *
 * Audio bytes are NOT committed to git — the clips are hosted on R2 and served
 * via `images.legendary-arena.com` under the `audio/sound-effects/` prefix (the
 * ewiki Sound Effects hosting rule). This module carries URLs only.
 *
 * @see WP-412 §C "Clip manifest"
 * @see DECISIONS.md D-24224 (audio-layer foundation; CC0-first, R2-hosted)
 */

import type { NotableGameEvent } from '../composables/useNotableEventStream';

/**
 * The twelve notable-event discriminators, derived type-only from the engine
 * union (via the `NotableGameEvent` alias) so this module never names the
 * engine union directly — the same runtime-safe-surface discipline
 * `useNotableEventStream` follows.
 */
export type SfxEventKey = NotableGameEvent['type'];

// why: audio bytes live on R2, served via images.legendary-arena.com under the
// audio/sound-effects/ prefix (the ewiki hosting rule) — never in git. Clip
// filenames use hyphens, not underscores (the repo image-URL convention).
const SFX_BASE_URL = 'https://images.legendary-arena.com/audio/sound-effects/';

/**
 * Exhaustive map of every `NotableGameEventType` variant to its CC0 clip URL.
 * The `Record<SfxEventKey, string>` type is the load-bearing drift pin: adding a
 * thirteenth engine event variant fails `vue-tsc` here until it is mapped, and the
 * `sfxManifest.test.ts` drift test fails if any of the twelve is unmapped or empty.
 */
export const sfxManifest: Record<SfxEventKey, string> = {
  fightResolved: `${SFX_BASE_URL}villain-defeated.mp3`,
  ambushResolved: `${SFX_BASE_URL}ambush.mp3`,
  schemeTwistResolved: `${SFX_BASE_URL}scheme-twist.mp3`,
  mastermindStrikeResolved: `${SFX_BASE_URL}master-strike.mp3`,
  // why: the Mastermind defeat is the match's peak reward, so it fires the full
  // victory-theme fanfare (`mastermind-defeated-win.mp3`) rather than the short
  // `mastermind-defeated.mp3` sting. This clip is larger (~1.3 MB vs ~20 KB), so
  // it adds weight to the up-front preload (audioEngine preloads every manifest
  // clip) — accepted so the fanfare plays instantly on the defeat, with no
  // load-latency at the victory moment.
  mastermindDefeated: `${SFX_BASE_URL}mastermind-defeated-win.mp3`,
  healResolved: `${SFX_BASE_URL}heal.mp3`,
  // why: WP-602 — the exhaustive Record forces the 7th variant (bystanderRevealed)
  // to carry a clip. The CC0 byte is operator-pending on R2 (same posture as every
  // clip here — WP-412/413/425 all shipped their URLs before the upload); a
  // not-yet-uploaded clip 404s on preload and no-ops, so the overlay ships complete
  // and the sting starts playing once the byte lands. Hyphenated filename per convention.
  bystanderRevealed: `${SFX_BASE_URL}bystander-revealed.mp3`,
  // why: WP-642 — the exhaustive Record forces the 8th variant (deckReshuffled)
  // to carry a clip. The CC0 byte is operator-pending on R2 (same posture as
  // every clip here — WP-412/413/425/602 all shipped their URLs before the
  // upload); a not-yet-uploaded clip 404s on preload and no-ops, so the overlay
  // + sting ship complete and the shuffle sound starts once the byte lands.
  // Hyphenated filename per convention.
  deckReshuffled: `${SFX_BASE_URL}deck-shuffled.mp3`,
  // why: WP-644 declared the 9th variant (strikeBlocked); the byte is now LIVE
  // on R2 — the metallic shield-block clang that plays alongside the WP-647
  // shield-block VfxOverlay burst, on the same notableEvents frame. Unlike the
  // CC0-sourced clips here it is ORIGINAL synthesis (no third-party/CC0
  // obligation — the cleanest commercial posture), generated deterministically
  // by ewiki/sound-effects/strike-blocked.py and encoded/uploaded via
  // scripts/upload-move-sfx-to-r2.mjs. Audio bytes are never committed to git
  // (D-24219, R2 is the sole audio surface). Hyphenated filename per convention.
  strikeBlocked: `${SFX_BASE_URL}strike-blocked.mp3`,
  // why: WP-672 — the exhaustive Record forces the 10th variant (transformResolved)
  // to carry a clip: the gamma "power-up" surge that plays alongside the transform
  // VfxOverlay beat, on the same notableEvents frame. The byte is operator-pending
  // on R2 (same posture as bystanderRevealed / deckReshuffled — WP-412/413/425/602/642
  // all shipped their URLs before the upload); a not-yet-uploaded clip 404s on preload
  // and no-ops, so the overlay + VFX ship complete and the surge sound starts once the
  // byte lands. Hyphenated filename per convention.
  transformResolved: `${SFX_BASE_URL}transform.mp3`,
  // why: WP-697 — the exhaustive Record forces the 11th variant (heroEffectResolved)
  // to carry a clip: a light "power-up" chime for an invisible-work hero effect
  // surfacing (v1 the Jade Giantess reveal-for-attack). The byte is operator-pending
  // on R2 (same posture as bystanderRevealed / deckReshuffled / transformResolved —
  // WP-412/413/425/602/642/672 all shipped their URLs before the upload); a
  // not-yet-uploaded clip 404s on preload and no-ops, so the overlay ships complete
  // and the chime starts once the byte lands. Hyphenated filename per convention.
  heroEffectResolved: `${SFX_BASE_URL}hero-ability.mp3`,
  // why: WP-746 — the exhaustive Record forces the 12th variant (excessiveViolenceFired)
  // to carry a clip: a harsh overspend "unleash" hit that plays alongside the crossed-swords
  // slash-burst VfxOverlay beat, on the same notableEvents frame. The byte is operator-pending
  // on R2 (same posture as bystanderRevealed / deckReshuffled / transformResolved /
  // heroEffectResolved — WP-412/413/425/602/642/672/697 all shipped their URLs before the
  // upload); a not-yet-uploaded clip 404s on preload and no-ops, so the overlay + VFX ship
  // complete and the hit starts once the byte lands. Hyphenated filename per convention.
  excessiveViolenceFired: `${SFX_BASE_URL}excessive-violence.mp3`,
};
